import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Box, Checkbox, CircularProgress, Paper, Typography } from "@mui/material";

import { AlertNote } from "./AlertNote";
import { Button } from "./Button";
import { useAuth } from "./contexts/AuthContext";
import { pluralize } from "../Pages/Plan/billingFormat";
import {
  BILLING_MEMBERS_QUERY_KEY,
  BILLING_QUERY_KEY,
  getBillingMembers,
  releaseUsers,
} from "../services/billingService";
import { PLAN_PATH } from "../Pages/Settings/settingsPaths";

const ROLE_LABELS = { agency_admin: "Admin", consultant: "Consultor" };

/**
 * Bloqueo para el admin cuando el equipo supera las plazas del plan, venga de
 * donde venga (bajada de plan, impago, fin de la prueba). Sale eligiendo quién
 * se queda o comprando plazas en /plan, donde el bloqueo no se muestra.
 */
export function UserSeatsGate() {
  const { user, interceptorReady, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const plan = user?.agencyPlan;
  const over = plan?.users_over_limit ?? 0;
  // La selección de clientes de Free va primero
  const visible = Boolean(
    interceptorReady &&
      user?.role === "agency_admin" &&
      over > 0 &&
      !plan?.requires_client_selection &&
      !location.pathname.startsWith(PLAN_PATH),
  );

  const [keptIds, setKeptIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const { data: members, isLoading, isError } = useQuery({
    queryKey: BILLING_MEMBERS_QUERY_KEY,
    queryFn: getBillingMembers,
    enabled: visible,
    staleTime: 0,
  });
  const removable = (members || []).filter((member) => member.removable);
  const fixedCount = (members || []).length - removable.length;
  const limit = plan?.users_limit ?? 0;
  const canRelease = fixedCount <= limit;
  const keptCount = fixedCount + keptIds.length;
  const leaving = removable.filter((member) => !keptIds.includes(member.id));
  // Quien no se puede quitar va primero: explica por qué ya hay plazas ocupadas
  const ordered = [...(members || [])].sort((a, b) => a.removable - b.removable);
  // Solo sin plazas para los dos protegidos: quien mira (otro admin) y el admin principal
  const owner = (members || []).find((member) => !member.removable && member.id !== user?.id);
  const full = keptCount >= limit && leaving.length > 0;

  useEffect(() => {
    if (!visible) setKeptIds([]);
  }, [visible]);

  if (!visible) return null;

  const toggle = (id) => {
    setError("");
    setKeptIds((current) => current.includes(id)
      ? current.filter((value) => value !== id)
      : keptCount < limit ? [...current, id] : current
    );
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      queryClient.setQueryData(BILLING_QUERY_KEY, await releaseUsers(leaving.map((member) => member.id)));
      // Released members drop out of Team, whose lists come from the generic `getData` hook
      queryClient.invalidateQueries({ queryKey: ["getData"] });
      await refreshUser();
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "No se ha podido guardar la selección.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: 200000, bgcolor: "rgba(15,23,42,.72)", display: "grid", placeItems: "center", p: 2 }}>
      <Paper sx={{ width: "100%", maxWidth: 620, maxHeight: "90vh", overflow: "auto", p: 3, borderRadius: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Elige {limit === 1 ? "quién sigue" : `las ${limit} personas que siguen`} en el equipo
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Tu plan {plan.plan_name} incluye {pluralize(limit, "plaza", "plazas")} y tienes{" "}
          {pluralize(plan.users_count, "usuario", "usuarios")}. Quien no elijas dejará de poder entrar en Markezy; no se
          borra nada: sus clientes, mensajes y horas se conservan. También puedes añadir plazas para mantenerlos a todos.
        </Typography>
        {error && <AlertNote variant="danger" sx={{ mb: 2 }}>{error}</AlertNote>}
        {isLoading ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 4 }}><CircularProgress /></Box>
        ) : isError ? (
          <AlertNote variant="danger">No se ha podido cargar el equipo.</AlertNote>
        ) : canRelease ? (
          <Box sx={{ display: "grid", gap: 1, mb: 3 }}>
            {ordered.map((member) => {
              const kept = !member.removable || keptIds.includes(member.id);
              return (
                <Box
                  key={member.id}
                  onClick={member.removable ? () => toggle(member.id) : undefined}
                  sx={{
                    display: "flex", alignItems: "center", p: 1.25, border: "1px solid",
                    borderColor: kept ? "primary.main" : "divider", borderRadius: 2,
                    cursor: member.removable ? "pointer" : "default",
                  }}
                >
                  <Checkbox checked={kept} disabled={!member.removable} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap>{member.name}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                      {ROLE_LABELS[member.role]} · {member.email}
                      {!member.removable && (member.id === user.id ? " · Tú, siempre conservas tu plaza" : " · Admin principal, siempre conserva su plaza")}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
            {full && (
              <AlertNote variant="info" sx={{ mt: 1 }}>
                {keptIds.length === 0
                  ? `${fixedCount === 1 ? "Tu plan solo incluye tu plaza" : "Las plazas de tu plan ya las ocupáis tú y el admin principal"}. Para mantener a alguien más, añade plazas.`
                  : "Ya has ocupado todas las plazas. Desmarca a alguien para elegir a otra persona, o añade plazas."}
              </AlertNote>
            )}
          </Box>
        ) : (
          <AlertNote variant="info" sx={{ mb: 3 }}>
            Tu plan incluye {pluralize(limit, "plaza", "plazas")} y la ocupa {owner?.name}, el admin principal. Pídele que
            elija quién sigue en el equipo, o añade plazas.
          </AlertNote>
        )}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
          <Button variant={canRelease ? "outline" : "default"} disabled={saving} onClick={() => navigate(`${PLAN_PATH}?add_users=1`)}>
            Añadir plazas
          </Button>
          {canRelease && (
            <Button onClick={save} disabled={saving || keptCount !== limit}>
              {saving ? "Guardando…" : `Guardar selección (${keptCount}/${limit})`}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
